import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  computed,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AnalysisStore } from '@features/campaigns/store/analysis-store';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import {
  AnalysisTable,
  AnalysisColumnVisibility,
} from '@shared/components/analysis-table/analysis-table';
import { Analysis } from '@shared/models/analysis.model';
import { InlineNotice } from '@shared/components/inline-notice/inline-notice';

@Component({
  selector: 'app-analyses-page',
  standalone: true,
  imports: [CommonModule, AnalysisTable, InlineNotice],
  providers: [],
  templateUrl: './analyses-page.html',
  styleUrl: './analyses-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysesPage implements OnInit {
  private readonly analysisStore = inject(AnalysisStore);
  private readonly currentOrgStore = inject(CurrentOrganizationStore);
  private readonly router = inject(Router);

  // Use store's loading state
  isLoading = this.analysisStore.isLoading;
  error = computed(() => this.analysisStore.loading().error);

  // Get current organization
  currentOrganization = computed(() => this.currentOrgStore.organization());
  organizationId = computed(
    () => this.currentOrgStore.organization()?.id || '',
  );

  /** Everything on — this page is where the final column set gets decided. */
  readonly columnVisibility: AnalysisColumnVisibility = {
    analysis: true,
    type: true,
    address: false,
    consumption: true,
    status: true,
    source: true,
    createdAt: true,
    execution: true,
  };

  async ngOnInit(): Promise<void> {
    await this.loadAnalyses();
  }

  async loadAnalyses(): Promise<void> {
    const organization = this.currentOrganization();
    if (!organization) {
      console.warn('[AnalysesPage] No organization selected');
      return;
    }

    // Use store to load analyses for organization (without campaign filter)
    await this.analysisStore.loadAnalysesByOrganization(organization.id);
  }

  async onAnalysisCreated(analysis: Analysis): Promise<void> {
    console.log('[AnalysesPage] Analysis created:', analysis);
    // Navigate to the analysis detail page
    const organization = this.currentOrganization();
    if (organization) {
      await this.router.navigate([
        '/organizations',
        organization.slug,
        'analyses',
        analysis.analysisId,
      ]);
    } else {
      // Fallback: reload the list
      await this.loadAnalyses();
    }
  }
}
